import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Chip,
  Grid,
  Skeleton,
  Stack,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import QuizIcon from '@mui/icons-material/Quiz';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import LayersIcon from '@mui/icons-material/Layers';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import BiotechIcon from '@mui/icons-material/Biotech';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import ScienceIcon from '@mui/icons-material/Science';
import TrackChangesIcon from '@mui/icons-material/TrackChanges';
import ViewInArIcon from '@mui/icons-material/ViewInAr';
import GrainIcon from '@mui/icons-material/Grain';
import MedicationIcon from '@mui/icons-material/Medication';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import { useApiContext } from '@/contexts/apiContext';

interface BuildOption {
  id: string;
  icon: React.ReactNode;
  title: string;
  description: string;
  tag?: string;
  actionType: 'navigate' | 'survey';
  target: string;
}

// Category 1: Non-Survey Build Methods
const NON_SURVEY_OPTIONS: BuildOption[] = [
  {
    id: 'manual',
    icon: <AccountTreeIcon sx={{ fontSize: 24 }} />,
    title: 'Build Manually (Visual Builder)',
    description:
      'Design and configure your AI pipeline step-by-step using our visual drag-and-drop node graph builder.',
    tag: 'Interactive Graph',
    actionType: 'navigate',
    target: '/pipelineBuilder',
  },
  {
    id: 'blueprint',
    icon: <LayersIcon sx={{ fontSize: 24 }} />,
    title: 'Use a Pipeline Blueprint',
    description:
      'Select from our library of pre-built, production-ready pipeline blueprints to kickstart your project instantly.',
    tag: 'Pre-built Template',
    actionType: 'navigate',
    target: '/marketplace?category=blueprint',
  },
];

// Category 2: Generic Setup Surveys & Prompts
const GENERIC_SURVEY_OPTIONS: BuildOption[] = [
  {
    id: 'userRequirements',
    icon: <AutoAwesomeIcon sx={{ fontSize: 24 }} />,
    title: 'Describe Your Use Case',
    description:
      'Tell us what you want to achieve in plain language and our AI agent will auto-generate a complete pipeline.',
    tag: 'AI Prompt',
    actionType: 'navigate',
    target: '/builderUserSurvey/userRequirements',
  },
  {
    id: 'quickSurvey',
    icon: <QuizIcon sx={{ fontSize: 24 }} />,
    title: 'General Setup Survey',
    description:
      'Answer guided questions about your data sources, processing requirements, and LLM preferences.',
    tag: 'Guided Wizard',
    actionType: 'survey',
    target: 'quickSurvey',
  },
];

const renderIconByName = (iconName?: string, surveyId?: string) => {
  const iconKey = (iconName || surveyId || '').toLowerCase();
  if (iconKey.includes('menubook') || iconKey.includes('literature')) return <MenuBookIcon sx={{ fontSize: 22 }} />;
  if (iconKey.includes('biotech') || iconKey.includes('sequence')) return <BiotechIcon sx={{ fontSize: 22 }} />;
  if (iconKey.includes('trackchanges') || iconKey.includes('target')) return <TrackChangesIcon sx={{ fontSize: 22 }} />;
  if (iconKey.includes('science') || iconKey.includes('virtual') || iconKey.includes('docking')) return <ScienceIcon sx={{ fontSize: 22 }} />;
  if (iconKey.includes('viewinar') || iconKey.includes('structure')) return <ViewInArIcon sx={{ fontSize: 22 }} />;
  if (iconKey.includes('grain') || iconKey.includes('genomics') || iconKey.includes('variant')) return <GrainIcon sx={{ fontSize: 22 }} />;
  if (iconKey.includes('medication') || iconKey.includes('repurposing') || iconKey.includes('drug')) return <MedicationIcon sx={{ fontSize: 22 }} />;
  if (iconKey.includes('rocketlaunch') || iconKey.includes('master')) return <RocketLaunchIcon sx={{ fontSize: 22 }} />;
  return <BiotechIcon sx={{ fontSize: 22 }} />;
};

const BuildOptions = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const apiContext = useApiContext();

  const [domainGroupedSurveys, setDomainGroupedSurveys] = useState<Record<string, any[]>>({});
  const [loadingSurveys, setLoadingSurveys] = useState<boolean>(true);

  useEffect(() => {
    const fetchSurveys = async () => {
      try {
        setLoadingSurveys(true);
        const surveys = await apiContext.getUserSurveys();
        
        const GENERIC_IDS = ['quickSurvey', 'userRequirements', 'requirementDescription'];
        const grouped: Record<string, any[]> = {};
        (surveys || []).forEach((s: any) => {
          if (
            GENERIC_IDS.includes(s.id) ||
            s.id.toLowerCase().includes('requirementdescription') ||
            s.id.toLowerCase().includes('userrequirement') ||
            (s.category && s.category.toLowerCase().includes('generic'))
          ) {
            return;
          }
          const cat = s.category || 'Domain-Specific Setup Wizards';
          if (!grouped[cat]) grouped[cat] = [];
          grouped[cat].push(s);
        });
        
        setDomainGroupedSurveys(grouped);
      } catch (err) {
        console.error('Failed to fetch user surveys from DB:', err);
      } finally {
        setLoadingSurveys(false);
      }
    };
    fetchSurveys();
  }, []);

  const close = () => navigate('/dashboard');

  const handleLaunch = (target: string, actionType: 'navigate' | 'survey') => {
    if (actionType === 'navigate') {
      navigate(target);
    } else {
      navigate(`/builderUserSurvey/${target}`);
    }
  };

  return (
    <Box
      sx={{
        width: '100%',
        minHeight: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        px: { xs: 2, sm: 4 },
        py: { xs: 3, md: 4 },
        bgcolor: 'background.default',
      }}
    >
      {/* Top Header Strip with Navigation */}
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ width: '100%', maxWidth: 1180, mb: 3 }}
      >
        <Button
          variant="text"
          startIcon={<ArrowBackIcon />}
          onClick={close}
          size="small"
          sx={{
            textTransform: 'none',
            fontWeight: 600,
            color: 'text.secondary',
          }}
        >
          Back to Dashboard
        </Button>
        <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 600 }}>
          Select any card below to launch its construction setup
        </Typography>
      </Stack>

      <Stack spacing={0.5} alignItems="center" sx={{ mb: 3.5, textAlign: 'center' }}>
        <Typography variant="h5" sx={{ fontWeight: 800, color: 'text.primary' }}>
          How would you like to build?
        </Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary', maxWidth: 640 }}>
          Choose a non-survey pipeline construction method, a generic setup wizard, or launch a domain-specific AI research wizard directly.
        </Typography>
      </Stack>

      {/* Main Categorized Content Container */}
      <Box sx={{ width: '100%', maxWidth: 1180, mb: 3 }}>
        <Grid container spacing={3.5}>
          {/* SECTION 1: Non-Survey Build Methods */}
          <Grid size={{ xs: 12, md: 6 }}>
            <Stack spacing={2}>
              <Stack direction="row" alignItems="center" spacing={1} sx={{ px: 0.5 }}>
                <AccountTreeIcon sx={{ color: theme.palette.primary.main, fontSize: 20 }} />
                <Typography variant="subtitle2" sx={{ fontWeight: 800, textTransform: 'uppercase', letterSpacing: 0.8, color: 'text.secondary', fontSize: '11px' }}>
                  1. Non-Survey Construction Methods
                </Typography>
              </Stack>

              <Grid container spacing={1.5}>
                {NON_SURVEY_OPTIONS.map((opt) => (
                  <Grid size={{ xs: 12 }} key={opt.id}>
                    <Box
                      onClick={() => handleLaunch(opt.target, opt.actionType)}
                      sx={{
                        p: 2,
                        borderRadius: '12px',
                        border: `1px solid ${theme.palette.divider}`,
                        bgcolor: alpha(theme.palette.background.paper, 0.6),
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          borderColor: theme.palette.primary.main,
                          bgcolor: alpha(theme.palette.primary.main, 0.03),
                          boxShadow: `0 4px 14px ${alpha(theme.palette.primary.main, 0.12)}`,
                        },
                      }}
                    >
                      <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1.5}>
                        <Box
                          sx={{
                            width: 40,
                            height: 40,
                            borderRadius: '10px',
                            bgcolor: alpha(theme.palette.primary.main, 0.06),
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: theme.palette.primary.main,
                            flexShrink: 0,
                          }}
                        >
                          {opt.icon}
                        </Box>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.25 }}>
                            <Typography sx={{ fontWeight: 700, fontSize: '14px', color: 'text.primary' }}>
                              {opt.title}
                            </Typography>
                            {opt.tag && (
                              <Chip
                                label={opt.tag}
                                size="small"
                                sx={{
                                  height: 18,
                                  fontSize: '9px',
                                  fontWeight: 700,
                                  bgcolor: alpha(theme.palette.primary.main, 0.1),
                                  color: theme.palette.primary.main,
                                }}
                              />
                            )}
                          </Stack>
                          <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '12px', lineHeight: 1.4 }}>
                            {opt.description}
                          </Typography>
                        </Box>
                        <Button
                          variant="contained"
                          color="primary"
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleLaunch(opt.target, opt.actionType);
                          }}
                          sx={{ textTransform: 'none', fontWeight: 700, borderRadius: '8px', fontSize: '11.5px', px: 2, flexShrink: 0 }}
                        >
                          Launch →
                        </Button>
                      </Stack>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </Stack>
          </Grid>

          {/* SECTION 2: Generic Setup Surveys & Prompts */}
          <Grid size={{ xs: 12, md: 6 }}>
            <Stack spacing={2}>
              <Stack direction="row" alignItems="center" spacing={1} sx={{ px: 0.5 }}>
                <QuizIcon sx={{ color: theme.palette.info.main, fontSize: 20 }} />
                <Typography variant="subtitle2" sx={{ fontWeight: 800, textTransform: 'uppercase', letterSpacing: 0.8, color: 'text.secondary', fontSize: '11px' }}>
                  2. Generic Setup Surveys & AI Prompts
                </Typography>
              </Stack>

              <Grid container spacing={1.5}>
                {GENERIC_SURVEY_OPTIONS.map((opt) => (
                  <Grid size={{ xs: 12 }} key={opt.id}>
                    <Box
                      onClick={() => handleLaunch(opt.target, opt.actionType)}
                      sx={{
                        p: 2,
                        borderRadius: '12px',
                        border: `1px solid ${theme.palette.divider}`,
                        bgcolor: alpha(theme.palette.background.paper, 0.6),
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          borderColor: theme.palette.info.main,
                          bgcolor: alpha(theme.palette.info.main, 0.03),
                          boxShadow: `0 4px 14px ${alpha(theme.palette.info.main, 0.12)}`,
                        },
                      }}
                    >
                      <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1.5}>
                        <Box
                          sx={{
                            width: 40,
                            height: 40,
                            borderRadius: '10px',
                            bgcolor: alpha(theme.palette.info.main, 0.06),
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: theme.palette.info.main,
                            flexShrink: 0,
                          }}
                        >
                          {opt.icon}
                        </Box>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.25 }}>
                            <Typography sx={{ fontWeight: 700, fontSize: '14px', color: 'text.primary' }}>
                              {opt.title}
                            </Typography>
                            {opt.tag && (
                              <Chip
                                label={opt.tag}
                                size="small"
                                sx={{
                                  height: 18,
                                  fontSize: '9px',
                                  fontWeight: 700,
                                  bgcolor: alpha(theme.palette.info.main, 0.1),
                                  color: theme.palette.info.main,
                                }}
                              />
                            )}
                          </Stack>
                          <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '12px', lineHeight: 1.4 }}>
                            {opt.description}
                          </Typography>
                        </Box>
                        <Button
                          variant="contained"
                          color="info"
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleLaunch(opt.target, opt.actionType);
                          }}
                          sx={{ textTransform: 'none', fontWeight: 700, borderRadius: '8px', fontSize: '11.5px', px: 2, flexShrink: 0 }}
                        >
                          Launch →
                        </Button>
                      </Stack>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </Stack>
          </Grid>

          {/* SECTION 3: Domain-Specific Setup Wizards */}
          <Grid size={{ xs: 12 }}>
            <Box sx={{ pt: 1 }}>
              {loadingSurveys ? (
                <Stack spacing={1.5}>
                  <Skeleton variant="text" width={240} height={28} />
                  <Grid container spacing={1.5}>
                    {[1, 2, 3, 4].map((n) => (
                      <Grid size={{ xs: 12, sm: 6, md: 3 }} key={n}>
                        <Skeleton variant="rounded" height={80} sx={{ borderRadius: '12px' }} />
                      </Grid>
                    ))}
                  </Grid>
                </Stack>
              ) : Object.keys(domainGroupedSurveys).length === 0 ? (
                <Box sx={{ p: 3, textAlign: 'center', borderRadius: '12px', border: `1px dashed ${theme.palette.divider}` }}>
                  <Typography variant="body2" color="text.secondary">
                    No custom domain surveys registered in database. Run the NIELIT seed script to populate.
                  </Typography>
                </Box>
              ) : (
                Object.entries(domainGroupedSurveys).map(([categoryName, surveys]) => (
                  <Stack spacing={2} key={categoryName} sx={{ mb: 2 }}>
                    <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ px: 0.5 }}>
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <BiotechIcon sx={{ color: theme.palette.secondary.main, fontSize: 22 }} />
                        <Typography variant="subtitle2" sx={{ fontWeight: 800, textTransform: 'uppercase', letterSpacing: 0.8, color: 'text.secondary', fontSize: '11px' }}>
                          3. {categoryName}
                        </Typography>
                      </Stack>
                      <Chip label={`${surveys.length} Domain Wizards`} color="secondary" size="small" sx={{ height: 20, fontSize: '9.5px', fontWeight: 700 }} />
                    </Stack>

                    <Grid container spacing={1.5}>
                      {surveys.map((survey: any) => (
                        <Grid size={{ xs: 12, sm: 6, md: 3 }} key={survey.id}>
                          <Box
                            onClick={() => handleLaunch(survey.id, 'survey')}
                            sx={{
                              p: 1.75,
                              height: '100%',
                              display: 'flex',
                              flexDirection: 'column',
                              justifyContent: 'space-between',
                              borderRadius: '12px',
                              border: `1px solid ${theme.palette.divider}`,
                              bgcolor: alpha(theme.palette.background.paper, 0.6),
                              cursor: 'pointer',
                              transition: 'all 0.2s ease',
                              '&:hover': {
                                borderColor: theme.palette.secondary.main,
                                bgcolor: alpha(theme.palette.secondary.main, 0.03),
                                transform: 'translateY(-2px)',
                                boxShadow: `0 6px 16px ${alpha(theme.palette.secondary.main, 0.15)}`,
                              },
                            }}
                          >
                            <Stack spacing={1}>
                              <Stack direction="row" alignItems="center" justifyContent="space-between">
                                <Box
                                  sx={{
                                    width: 36,
                                    height: 36,
                                    borderRadius: '8px',
                                    bgcolor: alpha(theme.palette.secondary.main, 0.08),
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    color: theme.palette.secondary.main,
                                  }}
                                >
                                  {renderIconByName(survey.icon, survey.id)}
                                </Box>
                                {survey.tag && (
                                  <Chip
                                    label={survey.tag}
                                    color="default"
                                    size="small"
                                    sx={{ height: 18, fontSize: '8.5px', fontWeight: 700 }}
                                  />
                                )}
                              </Stack>

                              <Box sx={{ minWidth: 0 }}>
                                <Typography variant="subtitle2" noWrap sx={{ fontWeight: 700, fontSize: '13px', color: 'text.primary', mb: 0.25 }}>
                                  {survey.name}
                                </Typography>
                                <Typography
                                  variant="body2"
                                  sx={{
                                    color: 'text.secondary',
                                    fontSize: '11px',
                                    lineHeight: 1.35,
                                    display: '-webkit-box',
                                    WebkitLineClamp: 2,
                                    WebkitBoxOrient: 'vertical',
                                    overflow: 'hidden',
                                  }}
                                >
                                  {survey.description}
                                </Typography>
                              </Box>
                            </Stack>

                            <Stack direction="row" justifyContent="flex-end" sx={{ pt: 1.25 }}>
                              <Button
                                variant="contained"
                                color="secondary"
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleLaunch(survey.id, 'survey');
                                }}
                                sx={{ textTransform: 'none', fontWeight: 700, borderRadius: '6px', fontSize: '11px', py: 0.35, px: 1.5 }}
                              >
                                Launch →
                              </Button>
                            </Stack>
                          </Box>
                        </Grid>
                      ))}
                    </Grid>
                  </Stack>
                ))
              )}
            </Box>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};

export default BuildOptions;
